import React from "react";
import { captureException } from "@/lib/posthog";
import {
  isChunkLoadError,
  attemptRecoveryReload,
} from "@/lib/chunk-recovery";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

function DefaultErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="flex flex-col items-center gap-4 text-center max-w-sm">
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold text-foreground">
          Something went wrong.
        </h1>
        <p className="text-muted-foreground text-sm">
          A quick reload usually fixes this.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 px-5 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    captureException(error);
    if (isChunkLoadError(error)) attemptRecoveryReload();
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultErrorFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
